import type { TripData } from "../types";

const categories = [
  "lodging",
  "transport",
  "coffee",
  "food",
  "attraction",
  "flight",
];
const ideaRegions = ["Santiago", "Atacama", "Puerto Natales", "Patagonia"];
const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const isString = (value: unknown): value is string => typeof value === "string";
const isDate = (value: unknown): value is string =>
  isString(value) &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(value)) &&
  new Date(value).toISOString().slice(0, 10) === value;
const optionalString = (value: unknown) =>
  value === undefined || isString(value);
const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString);
const safeUrl = (value: unknown) =>
  value === undefined || (isString(value) && /^https?:\/\//i.test(value));
const uniqueIds = (items: Record<string, unknown>[]) =>
  new Set(items.map((i) => i.id)).size === items.length;

function isVoteTally(value: unknown) {
  if (!isObject(value)) return false;
  for (const key of Object.keys(value)) {
    if (key !== "michael" && key !== "suzy") return false;
    const v = value[key];
    if (v !== undefined && v !== 1 && v !== -1) return false;
  }
  return true;
}

function isIdeas(value: unknown) {
  return (
    Array.isArray(value) &&
    value.every(
      (idea) =>
        isObject(idea) &&
        isString(idea.id) &&
        isString(idea.name) &&
        ideaRegions.includes(String(idea.region)) &&
        categories.includes(String(idea.category)) &&
        optionalString(idea.detail) &&
        optionalString(idea.pinId) &&
        (idea.suggestedDays === undefined ||
          (Array.isArray(idea.suggestedDays) &&
            idea.suggestedDays.every(isDate))),
    ) &&
    uniqueIds(value)
  );
}

function isVotes(value: unknown) {
  if (!isObject(value)) return false;
  return Object.values(value).every(isVoteTally);
}

/** Validate backups completely before replacing the user's saved itinerary. */
export function isTripData(value: unknown): value is TripData {
  if (!isObject(value) || !isObject(value.meta)) return false;
  const { meta, days, pins } = value;
  if (
    !isString(meta.name) ||
    !isString(meta.region) ||
    !isDate(meta.startDate) ||
    !isDate(meta.endDate) ||
    meta.startDate > meta.endDate ||
    !strings(meta.notes)
  )
    return false;
  if (
    !Array.isArray(meta.travelers) ||
    !meta.travelers.every(
      (t) =>
        isObject(t) &&
        ["id", "name", "shortName"].every((k) => isString(t[k])) &&
        optionalString(t.email),
    )
  )
    return false;
  if (
    !Array.isArray(days) ||
    days.length === 0 ||
    !days.every((d) => {
      if (
        !isObject(d) ||
        !isDate(d.date) ||
        d.date < String(meta.startDate) ||
        d.date > String(meta.endDate) ||
        !isString(d.title) ||
        !optionalString(d.summary) ||
        !optionalString(d.location)
      )
        return false;
      if (
        !Array.isArray(d.todos) ||
        !d.todos.every(
          (t) =>
            isObject(t) &&
            isString(t.id) &&
            isString(t.text) &&
            typeof t.done === "boolean",
        ) ||
        !uniqueIds(d.todos)
      )
        return false;
      return (
        Array.isArray(d.items) &&
        d.items.every(
          (i) =>
            isObject(i) &&
            isString(i.id) &&
            isString(i.title) &&
            [...categories, "note", "trek", "open"].includes(
              String(i.category),
            ) &&
            [undefined, "confirmed", "proposed", "tbd", "open"].includes(
              i.status as string | undefined,
            ) &&
            [i.time, i.endTime, i.detail, i.bookingRef, i.pinId, i.mapsQuery].every(optionalString) &&
            safeUrl(i.link) &&
            (i.travelers === undefined || strings(i.travelers)),
        ) &&
        uniqueIds(d.items)
      );
    }) ||
    new Set(days.map((d) => d.date)).size !== days.length
  )
    return false;
  if (!isIdeas(value.ideas) || !isVotes(value.votes)) return false;
  return (
    Array.isArray(pins) &&
    pins.every(
      (p) =>
        isObject(p) &&
        isString(p.id) &&
        isString(p.name) &&
        categories.includes(String(p.category)) &&
        typeof p.lat === "number" &&
        Number.isFinite(p.lat) &&
        Math.abs(p.lat) <= 90 &&
        typeof p.lng === "number" &&
        Number.isFinite(p.lng) &&
        Math.abs(p.lng) <= 180 &&
        [p.notes, p.imageAlt, p.imageCredit, p.mapsQuery, p.placeId].every(optionalString) &&
        safeUrl(p.imageUrl) &&
        (p.dayIds === undefined ||
          (Array.isArray(p.dayIds) && p.dayIds.every(isDate))),
    ) &&
    uniqueIds(pins) &&
    typeof value.version === "number" &&
    Number.isFinite(value.version)
  );
}
